import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Layout,
  Input,
  Select,
  DatePicker,
  Table,
  Row,
  Col,
  Card,
  Tag,
  Button,
  Tree,
  Typography,
  Space,
  Tooltip,
  Segmented,
  Spin,
  Empty,
  Modal,
  message,
} from 'antd';
import {
  SearchOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  BarChartOutlined,
  FormOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FolderOutlined,
  NodeIndexOutlined,
  CloseOutlined,
  LinkOutlined,
  FullscreenOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLanguage } from '../contexts/LanguageContext';
import { useCommerceUserTimezone, formatCommerceDateTime } from '../utils/commerceListDateUtils';
const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/** 文檔中心字級：在原本尺寸上加 2px */
const fs = (size) => size + 2;

const TREE_WIDTH_STORAGE_KEY = 'documentLibrary.treeWidth';
const TREE_WIDTH_DEFAULT = 280;
const TREE_WIDTH_MIN = 200;
const TREE_WIDTH_MAX = 520;
const DOCUMENT_LIBRARY_POLL_INTERVAL_MS = 30000;

function readStoredTreeWidth() {
  if (typeof window === 'undefined') return TREE_WIDTH_DEFAULT;
  const saved = parseInt(localStorage.getItem(TREE_WIDTH_STORAGE_KEY), 10);
  if (!Number.isFinite(saved)) return TREE_WIDTH_DEFAULT;
  return Math.max(TREE_WIDTH_MIN, Math.min(TREE_WIDTH_MAX, saved));
}

// ─── 授權圖片元件 ─────────────────────────────────────────────────────────────
function getArtifactPreviewUrl(item) {
  if (!item) return null;
  if (item.previewImagePath) {
    const path = String(item.previewImagePath).replace(/^\//, '');
    return `/Uploads/${path.split('/').map((s) => encodeURIComponent(s)).join('/')}`;
  }
  if (!item.sourceInstanceId) return null;
  const id = item.sourceInstanceId;
  switch (item.artifactType) {
    case 'Doc':
      return `/api/doc-instances/${id}/preview-image`;
    case 'Report':
      return `/api/report-instances/${id}/preview-image`;
    case 'eForm':
      return `/api/eform-instances/${id}/preview-image`;
    default:
      return null;
  }
}

function getThumbnailCacheKey(item) {
  if (!item) return '';
  return String(item.id || `${item.artifactType}-${item.sourceInstanceId}-${item.previewImagePath || ''}`);
}

/** 跨列/重渲染共用，避免 Table 重繪時重複 fetch 縮圖 */
const thumbnailBlobCache = new Map();
let thumbnailLoadsInFlight = 0;
const thumbnailLoadQueue = [];
const MAX_CONCURRENT_THUMBNAIL_LOADS = 4;

function runThumbnailLoadQueue() {
  while (thumbnailLoadsInFlight < MAX_CONCURRENT_THUMBNAIL_LOADS && thumbnailLoadQueue.length > 0) {
    const job = thumbnailLoadQueue.shift();
    if (job) job();
  }
}

function enqueueThumbnailLoad(run) {
  return new Promise((resolve, reject) => {
    const task = () => {
      thumbnailLoadsInFlight += 1;
      Promise.resolve()
        .then(run)
        .then(resolve, reject)
        .finally(() => {
          thumbnailLoadsInFlight -= 1;
          runThumbnailLoadQueue();
        });
    };
    thumbnailLoadQueue.push(task);
    runThumbnailLoadQueue();
  });
}

function ArtifactNoPreview({ style, label }) {
  const iconSize = Math.max(22, Math.min(40, Math.round((style?.height || style?.width || 56) * 0.28)));
  return (
    <div
      className="document-library-no-preview"
      style={{
        ...style,
        background: 'linear-gradient(180deg, #f5f5f5 0%, #ececec 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        color: '#bfbfbf',
        userSelect: 'none',
      }}
    >
      <PictureOutlined style={{ fontSize: iconSize, color: '#c4c4c4' }} />
      {label ? (
        <span style={{ fontSize: fs(9), lineHeight: `${fs(11)}px`, color: '#b5b5b5', fontWeight: 500 }}>
          {label}
        </span>
      ) : null}
    </div>
  );
}

const ArtifactThumbnail = React.memo(function ArtifactThumbnail({ item, token, style, alt = '', noPreviewLabel = 'No Image' }) {
  const cacheKey = getThumbnailCacheKey(item);
  const fetchUrl = getArtifactPreviewUrl(item);
  const [blobUrl, setBlobUrl] = useState(() => (cacheKey ? thumbnailBlobCache.get(cacheKey) : null) || null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
    if (!fetchUrl || !token || !cacheKey) {
      setBlobUrl(null);
      return undefined;
    }

    const cached = thumbnailBlobCache.get(cacheKey);
    if (cached) {
      setBlobUrl(cached);
      return undefined;
    }

    let cancelled = false;
    enqueueThumbnailLoad(async () => {
      const res = await fetch(fetchUrl, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
      if (!res.ok) return;
      const blob = await res.blob();
      if (!blob.type.startsWith('image/') || blob.size === 0) return;
      const url = URL.createObjectURL(blob);
      thumbnailBlobCache.set(cacheKey, url);
      if (!cancelled) setBlobUrl(url);
    }).catch(() => { /* ignore */ });

    return () => { cancelled = true; };
  }, [cacheKey, fetchUrl, token]);

  if (!fetchUrl || !blobUrl || imageError) {
    return <ArtifactNoPreview style={style} label={noPreviewLabel} />;
  }
  return (
    <img
      src={blobUrl}
      alt={alt}
      style={{ objectFit: 'cover', ...style }}
      onError={() => setImageError(true)}
    />
  );
}, (prev, next) => (
  getThumbnailCacheKey(prev.item) === getThumbnailCacheKey(next.item)
    && prev.token === next.token
    && prev.noPreviewLabel === next.noPreviewLabel
));

// ─── 產物類型圖示 ─────────────────────────────────────────────────────────────
function ArtifactTypeIcon({ type, format, style }) {
  const s = style || {};
  if (type === 'eForm') return <FormOutlined style={{ color: '#7234CF', ...s }} />;
  if (type === 'Report') return <BarChartOutlined style={{ color: '#52c41a', ...s }} />;
  if (type === 'Doc') {
    const f = (format || '').toLowerCase();
    if (f === 'pdf') return <FilePdfOutlined style={{ color: '#f5222d', ...s }} />;
    if (f === 'xlsx' || f === 'xls') return <FileExcelOutlined style={{ color: '#52c41a', ...s }} />;
    if (f === 'pptx' || f === 'ppt') return <FilePptOutlined style={{ color: '#fa8c16', ...s }} />;
    return <FileWordOutlined style={{ color: '#1677ff', ...s }} />;
  }
  return <FileTextOutlined style={s} />;
}

function typeColor(type) {
  if (type === 'eForm') return 'purple';
  if (type === 'Report') return 'green';
  if (type === 'Doc') return 'blue';
  return 'default';
}

// ─── Display Values 顯示元件（標籤 + 值 的小 badge 列）──────────────────────
function DisplayValueMoreIndicator({ hiddenValues, valueSeparator }) {
  if (!hiddenValues || hiddenValues.length === 0) return null;
  return (
    <Tooltip
      trigger={['hover', 'click']}
      title={(
        <div className="document-library-dv-tooltip">
          {hiddenValues.map((kv, i) => (
            <div key={i}>
              <span style={{ color: 'rgba(255,255,255,0.65)' }}>{kv.label}{valueSeparator}</span>
              <span>{kv.value}</span>
            </div>
          ))}
        </div>
      )}
    >
      <span
        className="document-library-dv-more"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        +{hiddenValues.length}
      </span>
    </Tooltip>
  );
}

function DisplayValueBadges({ values = [], maxShow = 4, valueSeparator = '：' }) {
  if (!values || values.length === 0) return null;
  const shown = values.slice(0, maxShow);
  const hidden = values.slice(maxShow);
  return (
    <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
      {shown.map((kv, i) => (
        <span key={i} style={{
          fontSize: fs(11), lineHeight: `${fs(16)}px`,
          background: '#f5f5f5', border: '1px solid #e8e8e8',
          borderRadius: 3, padding: '0 5px',
          maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          display: 'inline-block'
        }}>
          <span style={{ color: '#999' }}>{kv.label}{valueSeparator}</span>
          <span style={{ color: '#333', fontWeight: 500 }}>{kv.value}</span>
        </span>
      ))}
      <DisplayValueMoreIndicator hiddenValues={hidden} valueSeparator={valueSeparator} />
    </div>
  );
}

// ─── PV 摘要標籤（樹節點用，單行顯示）──────────────────────────────────────
function PvLabelInline({ labels = [] }) {
  if (!labels || labels.length === 0) return null;
  const shown = labels.slice(0, 3);
  const fullText = shown.map((kv) => `${kv.label}: ${kv.value}`).join(' | ');
  return (
    <span className="document-library-tree-pv-labels" title={fullText} style={{ fontSize: fs(11), color: '#7234CF' }}>
      {shown.map((kv, i) => (
        <span key={i}>
          {i > 0 && <span style={{ color: '#ccc' }}> | </span>}
          <span style={{ color: '#555' }}>{kv.label}:</span>
          <span style={{ fontWeight: 600, marginLeft: 2 }}>{kv.value}</span>
        </span>
      ))}
    </span>
  );
}

// ─── 日期快捷 ─────────────────────────────────────────────────────────────────
function presetToDates(val) {
  const now = dayjs();
  switch (val) {
    case 'today': return [now.startOf('day'), now.endOf('day')];
    case '7d': return [now.subtract(7, 'day').startOf('day'), now.endOf('day')];
    case '30d': return [now.subtract(30, 'day').startOf('day'), now.endOf('day')];
    case 'thisYear': return [now.startOf('year'), now.endOf('year')];
    case 'lastYear': return [now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')];
    default: return [null, null];
  }
}

/** 依可視寬度決定 tiles 每列數量 */
function getTileColumns(width) {
  if (width >= 3200) return 6;
  if (width >= 2560) return 5;
  if (width >= 2100) return 5;
  if (width >= 1920) return 4;
  if (width >= 1440) return 4;
  if (width >= 1024) return 3;
  if (width >= 640) return 2;
  return 1;
}

function useWindowWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1920
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return width;
}

// ─── 把 openUrl 轉換成不帶 SideMenu 的 embed URL ────────────────────────────
// /report-instance/{id}  → /preview/report-instance/{id}  (已有 preview 路由)
// /doc-instance/{id}     → /preview/doc-instance/{id}     (已有 preview 路由)
// /eform-instance/{id}   → /eform-instance/{id}           (已設定匿名/無 layout 路由)
function toEmbedUrl(openUrl) {
  if (!openUrl) return openUrl;
  return openUrl
    .replace(/^\/report-instance\//, '/preview/report-instance/')
    .replace(/^\/doc-instance\//, '/preview/doc-instance/');
}

// ─── 全螢幕 Artifact Modal ────────────────────────────────────────────────────
// 儲存、匯出PDF、審批按鈕已移入 EFormInstancePage 自身的 header toolbar
function ArtifactFullscreenModal({ item, onClose, t }) {
  if (!item) return null;
  const url = toEmbedUrl(item.openUrl);
  return (
    <Modal
      open={!!item}
      onCancel={onClose}
      footer={null}
      width="100vw"
      style={{ top: 0, padding: 0, maxWidth: '100vw' }}
      styles={{ body: { padding: 0, height: '100vh', overflow: 'hidden' }, content: { padding: 0, borderRadius: 0 } }}
      closeIcon={
        <div
          style={{
            position: 'fixed', top: 12, right: 16, zIndex: 9999,
            display: 'flex', alignItems: 'center',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              borderRadius: 20, width: 36, height: 36,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          />
        </div>
      }
      destroyOnClose
      maskClosable={false}
    >
      {/* iframe 嵌入現有頁面；EFormInstancePage 自帶完整 toolbar（儲存/匯出PDF/審批） */}
      <iframe
        src={url}
        title={item.instanceName}
        style={{ width: '100%', height: '100vh', border: 'none', display: 'block' }}
        allow="fullscreen"
      />
    </Modal>
  );
}

function translateStatus(status, t) {
  if (!status) return null;
  const map = {
    Approved: 'statusApproved',
    Completed: 'statusCompleted',
    Rejected: 'statusRejected',
    Pending: 'statusPending',
    Submitted: 'statusSubmitted',
  };
  const key = map[status];
  return key ? t(`documentLibrary.${key}`) : status;
}

// ─────────────────────────────────────────────────────────────────────────────
// 主元件
// ─────────────────────────────────────────────────────────────────────────────
export default function DocumentLibraryPage() {
  const { t } = useLanguage();
  const token = localStorage.getItem('token') || '';
  const userTimezoneOffset = useCommerceUserTimezone();
  const windowWidth = useWindowWidth();
  const tileColumns = getTileColumns(windowWidth);

  const formatDisplayTime = useCallback(
    (value, format = 'MM-DD HH:mm') => formatCommerceDateTime(value, userTimezoneOffset, format),
    [userTimezoneOffset]
  );

  const valueSeparator = t('documentLibrary.valueSeparator');
  const noPreviewLabel = t('documentLibrary.noPreviewImage');

  const datePresets = useMemo(() => ([
    { label: t('documentLibrary.datePresetToday'), value: 'today' },
    { label: t('documentLibrary.datePreset7d'), value: '7d' },
    { label: t('documentLibrary.datePreset30d'), value: '30d' },
    { label: t('documentLibrary.datePresetThisYear'), value: 'thisYear' },
    { label: t('documentLibrary.datePresetLastYear'), value: 'lastYear' },
  ]), [t]);

  const artifactTypeOptions = useMemo(() => ([
    { label: t('documentLibrary.typeEForm'), value: 'eForm' },
    { label: t('documentLibrary.typeDoc'), value: 'Doc' },
    { label: t('documentLibrary.typeReport'), value: 'Report' },
  ]), [t]);

  // ── 狀態 ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeCollapsed, setTreeCollapsed] = useState(false);
  const [treeWidth, setTreeWidth] = useState(readStoredTreeWidth);
  const [treeData, setTreeData] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 24;
  const [viewMode, setViewMode] = useState('list');

  // 篩選
  const [searchText, setSearchText] = useState('');
  const [artifactTypes, setArtifactTypes] = useState([]);
  const [initiatedBy, setInitiatedBy] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [datePreset, setDatePreset] = useState(null);
  const [definitionId, setDefinitionId] = useState(null);
  const [executionId, setExecutionId] = useState(null);

  const [initiatedByOptions, setInitiatedByOptions] = useState([]);
  const [openItem, setOpenItem] = useState(null); // 全螢幕開啟的 artifact
  const [totalCountPulse, setTotalCountPulse] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [batchDownloading, setBatchDownloading] = useState(false);
  const searchTimerRef = useRef(null);
  const pageRef = useRef(1);
  const prevTotalRef = useRef(0);
  const itemsPollInFlightRef = useRef(false);
  const totalPulseTimerRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // ── 載入目錄樹 ────────────────────────────────────────────────────────────
  const loadTree = useCallback(async () => {
    setTreeLoading(true);
    try {
      const res = await fetch('/api/document-library/tree', { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('documentLibrary.errorTreeLoad'));

      const nodes = (data || []).map((def) => ({
        key: `def-${def.workflowDefinitionId}`,
        title: (
          <Tooltip title={`${def.workflowDefinitionName} (${def.totalCount})`}>
            <div className="document-library-tree-def">
              <FolderOutlined className="document-library-tree-icon" style={{ color: '#7234CF' }} />
              <span className="document-library-tree-ellipsis document-library-tree-def-name">{def.workflowDefinitionName}</span>
              <span className="document-library-tree-def-count">({def.totalCount})</span>
            </div>
          </Tooltip>
        ),
        definitionId: def.workflowDefinitionId,
        children: (def.executions || []).map((ex) => {
          const pvLabels = ex.pvLabels || [];
          const typeCounts = [
            ex.eFormCount > 0 && t('documentLibrary.treeEFormCount', { count: ex.eFormCount }),
            ex.docCount > 0 && t('documentLibrary.treeDocCount', { count: ex.docCount }),
            ex.reportCount > 0 && t('documentLibrary.treeReportCount', { count: ex.reportCount }),
          ].filter(Boolean).join(' ');
          const execTitle = `#${ex.workflowExecutionId}${typeCounts ? ` ${typeCounts}` : ''}`;
          return {
            key: `ex-${ex.workflowExecutionId}`,
            title: (
              <Tooltip title={execTitle}>
                <div className="document-library-tree-ex">
                  <div className="document-library-tree-ex-main">
                    <NodeIndexOutlined className="document-library-tree-icon" style={{ color: '#999', fontSize: fs(12) }} />
                    <span className="document-library-tree-ex-id">#{ex.workflowExecutionId}</span>
                    {typeCounts ? (
                      <span className="document-library-tree-ellipsis document-library-tree-ex-counts">{typeCounts}</span>
                    ) : null}
                  </div>
                  {pvLabels.length > 0 && (
                    <div className="document-library-tree-ex-pv">
                      <PvLabelInline labels={pvLabels} />
                    </div>
                  )}
                </div>
              </Tooltip>
            ),
            definitionId: def.workflowDefinitionId,
            executionId: ex.workflowExecutionId,
            isLeaf: true,
          };
        }),
      }));
      setTreeData(nodes);
    } catch (e) {
      message.error(e.message);
    } finally {
      setTreeLoading(false);
    }
  }, [token, t]);

  // ── 載入使用者下拉 ────────────────────────────────────────────────────────
  const loadInitiatedBy = useCallback(async () => {
    try {
      const res = await fetch('/api/document-library/initiated-by', { headers: authHeaders });
      const data = await res.json();
      if (res.ok) setInitiatedByOptions((data || []).map((v) => ({ label: v, value: v })));
    } catch { /* ignore */ }
  }, [token]);

  // ── 搜尋 ─────────────────────────────────────────────────────────────────
  const loadItems = useCallback(async (pg = 1, { silent = false } = {}) => {
    if (silent) {
      if (itemsPollInFlightRef.current) return;
      itemsPollInFlightRef.current = true;
    } else {
      setLoading(true);
    }

    try {
      const qs = new URLSearchParams();
      qs.set('page', String(pg));
      qs.set('pageSize', String(pageSize));
      if (searchText.trim()) qs.set('search', searchText.trim());
      if (artifactTypes.length > 0) qs.set('artifactType', artifactTypes.join(','));
      if (initiatedBy) qs.set('initiatedBy', initiatedBy);
      const [df, dt] = dateRange;
      if (df) qs.set('dateFrom', df.toISOString());
      if (dt) qs.set('dateTo', dt.toISOString());
      if (definitionId) qs.set('workflowDefinitionId', definitionId);
      if (executionId) qs.set('workflowExecutionId', executionId);

      const res = await fetch(`/api/document-library/search?${qs.toString()}`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('documentLibrary.errorSearch'));

      const newTotal = data.total || 0;
      if (silent && newTotal > prevTotalRef.current && prevTotalRef.current > 0) {
        setTotalCountPulse(true);
        if (totalPulseTimerRef.current) clearTimeout(totalPulseTimerRef.current);
        totalPulseTimerRef.current = setTimeout(() => setTotalCountPulse(false), 2500);
      }
      prevTotalRef.current = newTotal;
      setTotal(newTotal);

      if (!silent || pg === pageRef.current) {
        setItems(data.data || []);
      }
    } catch (e) {
      if (!silent) message.error(e.message);
    } finally {
      if (silent) itemsPollInFlightRef.current = false;
      else setLoading(false);
    }
  }, [token, searchText, artifactTypes, initiatedBy, dateRange, definitionId, executionId, t]);

  // ── 初始化 ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadTree();
    loadInitiatedBy();
  }, [loadTree, loadInitiatedBy]);

  useEffect(() => {
    setPage(1);
    pageRef.current = 1;
    prevTotalRef.current = 0;
    setTotalCountPulse(false);
    loadItems(1);
  }, [artifactTypes, initiatedBy, dateRange, definitionId, executionId]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      loadItems(pageRef.current, { silent: true });
    }, DOCUMENT_LIBRARY_POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [loadItems]);

  useEffect(() => () => {
    if (totalPulseTimerRef.current) clearTimeout(totalPulseTimerRef.current);
  }, []);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchText(v);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setPage(1); loadItems(1); }, 400);
  };

  const handleDatePreset = (val) => {
    setDatePreset(val);
    const [df, dt] = presetToDates(val);
    setDateRange([df, dt]);
  };

  const handleTreeSelect = (_, { node }) => {
    if (node.executionId) {
      setDefinitionId(node.definitionId);
      setExecutionId(node.executionId);
    } else {
      setDefinitionId(node.definitionId);
      setExecutionId(null);
    }
  };

  const clearLocation = () => { setDefinitionId(null); setExecutionId(null); };

  const handleTreeResizeStart = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = treeWidth;
    let currentWidth = startWidth;

    const onMove = (moveEvent) => {
      currentWidth = Math.max(
        TREE_WIDTH_MIN,
        Math.min(TREE_WIDTH_MAX, startWidth + moveEvent.clientX - startX),
      );
      setTreeWidth(currentWidth);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem(TREE_WIDTH_STORAGE_KEY, String(currentWidth));
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [treeWidth]);

  // 篩選條件變動時清除多選
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [artifactTypes, initiatedBy, dateRange, definitionId, executionId, searchText]);

  // 開啟文件：全螢幕 Modal
  const openArtifact = useCallback((item) => setOpenItem(item), []);

  const downloadDoc = useCallback((item) => {
    window.open(`/api/doc-instances/${item.sourceInstanceId}/file`, '_blank');
  }, []);

  const downloadFileAuth = useCallback(async (url, fallbackFilename, body) => {
    const res = await fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { ...authHeaders, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      credentials: 'include',
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) {
      let msg = res.statusText;
      try { const d = await res.json(); if (d.error) msg = d.error; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition');
    let name = fallbackFilename;
    if (cd) {
      const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
      const quoted = /filename="([^"]+)"/i.exec(cd);
      const plain = /filename=([^;\n]+)/i.exec(cd);
      if (utf8) { try { name = decodeURIComponent(utf8[1]); } catch { name = fallbackFilename; } }
      else if (quoted) name = quoted[1].trim();
      else if (plain) name = plain[1].replace(/"/g, '').trim();
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = name || fallbackFilename || 'download';
    a.click();
    URL.revokeObjectURL(objectUrl);
  }, [authHeaders]);

  const handleBatchDownload = useCallback(async () => {
    if (!selectedRowKeys.length) return;
    setBatchDownloading(true);
    try {
      await downloadFileAuth(
        '/api/document-library/batch-download-zip',
        'documents.zip',
        { ids: selectedRowKeys }
      );
      setSelectedRowKeys([]);
    } catch (e) {
      message.error(e.message);
    } finally {
      setBatchDownloading(false);
    }
  }, [selectedRowKeys, downloadFileAuth]);

  // ── 表格列定義 ────────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: t('documentLibrary.colThumbnail'),
      dataIndex: 'previewImagePath',
      width: 72,
      fixed: 'left',
      align: 'left',
      render: (_, item) => (
        <ArtifactThumbnail item={item} token={token} noPreviewLabel={noPreviewLabel} style={{ width: 56, height: 56, borderRadius: 4 }} />
      ),
    },
    {
      title: t('documentLibrary.colNameKeyValues'),
      dataIndex: 'instanceName',
      ellipsis: false,
      align: 'left',
      onCell: () => ({ style: { textAlign: 'left' } }),
      onHeaderCell: () => ({ style: { textAlign: 'left' } }),
      render: (name, item) => (
        <div className="document-library-name-cell">
          <div className="document-library-name-row">
            <ArtifactTypeIcon type={item.artifactType} format={item.outputFormat} />
            <Typography.Link
              className="document-library-name-link"
              title={name}
              onClick={(e) => {
                e.preventDefault();
                openArtifact(item);
              }}
            >
              {name}
            </Typography.Link>
            <Tag color={typeColor(item.artifactType)} className="document-library-type-tag">
              {item.artifactType}{item.outputFormat ? ` (${item.outputFormat})` : ''}
            </Tag>
          </div>
          <DisplayValueBadges values={item.displayValues} maxShow={3} valueSeparator={valueSeparator} />
        </div>
      ),
    },
    {
      title: t('documentLibrary.colWorkflow'),
      dataIndex: 'workflowDefinitionName',
      width: 160,
      ellipsis: true,
      align: 'left',
      render: (defName, item) => (
        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <div style={{ fontSize: fs(12) }} title={defName || t('documentLibrary.definitionFallback', { id: item.workflowDefinitionId })}>
            {defName || t('documentLibrary.definitionFallback', { id: item.workflowDefinitionId })}
          </div>
          <div style={{ fontSize: fs(11), color: '#999' }}>{t('documentLibrary.executionLabel', { id: item.workflowExecutionId })}</div>
        </div>
      ),
    },
    {
      title: t('documentLibrary.colUser'),
      dataIndex: 'initiatedBy',
      width: 110,
      ellipsis: true,
      render: (v) => v
        ? <Text style={{ fontSize: fs(12) }} ellipsis title={v}>{v}</Text>
        : <Text type="secondary">{t('documentLibrary.emptyValue')}</Text>,
    },
    {
      title: t('documentLibrary.colStatus'),
      dataIndex: 'status',
      width: 96,
      render: (s) => {
        const colorMap = { Approved: 'green', Completed: 'green', Rejected: 'red', Pending: 'gold', Submitted: 'blue' };
        const label = translateStatus(s, t) || t('documentLibrary.emptyValue');
        return <Tag color={colorMap[s] || 'default'} style={{ margin: 0 }}>{label}</Tag>;
      },
    },
    {
      title: t('documentLibrary.colUpdated'),
      dataIndex: 'updatedAt',
      width: 110,
      render: (v) => (
        <Text style={{ fontSize: fs(11), whiteSpace: 'nowrap' }} type="secondary">
          {formatDisplayTime(v)}
        </Text>
      ),
    },
    {
      title: t('documentLibrary.colActions'),
      width: 88,
      fixed: 'right',
      render: (_, item) => (
        <Space size={4}>
          <Tooltip title={t('documentLibrary.open')}>
            <Button size="small" icon={<FullscreenOutlined />} onClick={() => openArtifact(item)} />
          </Tooltip>
          {item.artifactType === 'Doc' && (
            <Tooltip title={t('documentLibrary.download')}>
              <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadDoc(item)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ], [t, token, valueSeparator, formatDisplayTime, openArtifact, downloadDoc, noPreviewLabel]);

  // ── 格狀卡片 ─────────────────────────────────────────────────────────────
  const GridView = () => (
    <div
      style={{
        marginTop: 8,
        display: 'grid',
        gridTemplateColumns: `repeat(${tileColumns}, minmax(0, 1fr))`,
        gap: 16,
      }}
    >
      {items.map((item) => (
        <Card
          key={item.id}
          className="document-library-grid-card"
          hoverable
          styles={{ body: { padding: '12px 14px 14px' }, cover: { margin: 0 } }}
          cover={
            <div
              className="document-library-grid-preview"
              onClick={() => openArtifact(item)}
            >
              {item.previewImagePath || item.sourceInstanceId
                ? (
                  <ArtifactThumbnail
                    item={item}
                    token={token}
                    noPreviewLabel={noPreviewLabel}
                    style={{ width: '100%', height: '100%', borderRadius: 10 }}
                  />
                )
                : (
                  <ArtifactNoPreview
                    style={{ width: '100%', height: '100%', borderRadius: 10 }}
                    label={noPreviewLabel}
                  />
                )}
            </div>
          }
          onClick={() => openArtifact(item)}
        >
          <div className="document-library-grid-body">
            <div className="document-library-grid-title-row">
              <ArtifactTypeIcon
                type={item.artifactType}
                format={item.outputFormat}
                style={{ fontSize: fs(14), flexShrink: 0 }}
              />
              <Text ellipsis style={{ fontSize: fs(12), fontWeight: 600, lineHeight: 1.5, flex: 1, minWidth: 0 }} title={item.instanceName}>
                {item.instanceName}
              </Text>
            </div>
            <Tag color={typeColor(item.artifactType)} style={{ fontSize: fs(10), margin: 0, alignSelf: 'flex-start' }}>
              {item.artifactType}{item.outputFormat ? ` (${item.outputFormat})` : ''}
            </Tag>
            {item.displayValues?.length > 0 && (
              <div className="document-library-grid-meta">
                {item.displayValues.slice(0, 3).map((kv, i) => (
                  <div
                    key={i}
                    className="document-library-grid-meta-line"
                    style={{ fontSize: fs(10), color: '#555' }}
                  >
                    <span style={{ color: '#999' }}>{kv.label}{valueSeparator}</span>
                    <span style={{ fontWeight: 600 }}>{kv.value}</span>
                  </div>
                ))}
                <DisplayValueMoreIndicator
                  hiddenValues={item.displayValues.slice(3)}
                  valueSeparator={valueSeparator}
                />
              </div>
            )}
            <Text type="secondary" style={{ fontSize: fs(10), lineHeight: 1.6, marginTop: 2 }}>
              {formatDisplayTime(item.updatedAt)}
            </Text>
          </div>
        </Card>
      ))}
    </div>
  );

  // ── 渲染 ─────────────────────────────────────────────────────────────────
  return (
    <Layout className="document-library-page" style={{ minHeight: '100vh', background: '#fff' }}>
      <div className="document-library-layout-row">
        {/* 左側目錄樹（可收合、可拖曳調整寬度） */}
        {!treeCollapsed && (
          <Sider
            width={treeWidth}
            collapsedWidth={0}
            collapsed={false}
            trigger={null}
            className="document-library-tree-sider"
            style={{
              background: '#fafafa',
              borderRight: '1px solid #f0f0f0',
              overflow: 'auto',
              padding: '16px 8px',
              flex: `0 0 ${treeWidth}px`,
              maxWidth: treeWidth,
              minWidth: treeWidth,
            }}
          >
            <Space style={{ marginBottom: 8, paddingLeft: 4, width: '100%', justifyContent: 'space-between' }}>
              <Space size={4}>
                <Title level={5} style={{ margin: 0, fontSize: fs(16) }}>{t('documentLibrary.treeTitle')}</Title>
                <Button size="small" icon={<ReloadOutlined />} type="text" onClick={loadTree} />
              </Space>
              <Tooltip title={t('documentLibrary.collapseTree')}>
                <Button size="small" type="text" icon={<MenuFoldOutlined />} onClick={() => setTreeCollapsed(true)} />
              </Tooltip>
            </Space>

            {(definitionId || executionId) && (
              <Button size="small" onClick={clearLocation} style={{ marginBottom: 8, width: '100%' }}>
                {t('documentLibrary.clearLocationFilter')}
              </Button>
            )}

            <Spin spinning={treeLoading} size="small">
              {treeData.length === 0 && !treeLoading
                ? <Empty description={t('documentLibrary.noData')} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 32 }} />
                : (
                  <Tree
                    className="document-library-tree"
                    treeData={treeData}
                    onSelect={handleTreeSelect}
                    selectedKeys={executionId ? [`ex-${executionId}`] : definitionId ? [`def-${definitionId}`] : []}
                    defaultExpandAll={treeData.length <= 5}
                    blockNode
                  />
                )
              }
            </Spin>
          </Sider>
        )}

        {!treeCollapsed && (
          <div
            className="document-library-tree-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label={t('documentLibrary.resizeTree')}
            onMouseDown={handleTreeResizeStart}
          >
            <div className="document-library-tree-resizer-handle" />
          </div>
        )}

        {/* 主內容區 */}
        <Content style={{ padding: '16px 20px', minWidth: 0, overflow: 'hidden', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          {treeCollapsed && (
            <Tooltip title={t('documentLibrary.expandTree')}>
              <Button icon={<MenuUnfoldOutlined />} onClick={() => setTreeCollapsed(false)}>
                {t('documentLibrary.treeTitle')}
              </Button>
            </Tooltip>
          )}
          <Input
            prefix={<SearchOutlined />}
            placeholder={t('documentLibrary.searchPlaceholder')}
            value={searchText}
            onChange={handleSearchChange}
            allowClear
            size="large"
            style={{ flex: 1 }}
          />
        </div>

        {/* 篩選列 */}
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
          <Col>
            <Select
              mode="multiple"
              placeholder={t('documentLibrary.filterType')}
              style={{ minWidth: 140 }}
              value={artifactTypes}
              onChange={setArtifactTypes}
              allowClear
              options={artifactTypeOptions}
            />
          </Col>
          <Col>
            <Select
              placeholder={t('documentLibrary.filterUser')}
              style={{ minWidth: 160 }}
              value={initiatedBy}
              onChange={setInitiatedBy}
              allowClear
              options={initiatedByOptions}
              showSearch
              optionFilterProp="label"
            />
          </Col>
          <Col>
            <Select
              placeholder={t('documentLibrary.filterModifiedDate')}
              style={{ minWidth: 130 }}
              value={datePreset}
              onChange={handleDatePreset}
              allowClear
              onClear={() => { setDatePreset(null); setDateRange([null, null]); }}
              options={datePresets}
            />
          </Col>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(vals) => { setDatePreset(null); setDateRange(vals || [null, null]); }}
              format="YYYY-MM-DD"
              allowClear
            />
          </Col>
          {(definitionId || executionId) && (
            <Col>
              <Tag
                closable
                onClose={clearLocation}
                color="purple"
                style={{ lineHeight: '30px', padding: '0 8px' }}
              >
                {executionId
                  ? t('documentLibrary.executionLabel', { id: executionId })
                  : t('documentLibrary.definitionFallback', { id: definitionId })}
              </Tag>
            </Col>
          )}
          <Col style={{ marginLeft: 'auto' }}>
            <Segmented
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'list', icon: <UnorderedListOutlined /> },
                { value: 'grid', icon: <AppstoreOutlined /> },
              ]}
            />
          </Col>
        </Row>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, minHeight: 32 }}>
          <Text
            type="secondary"
            className={`document-library-total-count${totalCountPulse ? ' document-library-total-count--pulse' : ''}`}
            style={{ fontSize: fs(12) }}
          >
            {t('documentLibrary.totalDocuments', { count: total })}
          </Text>
          {selectedRowKeys.length > 0 && (
            <>
              <Text style={{ fontSize: fs(12), color: '#7234CF', fontWeight: 600 }}>
                {t('documentLibrary.selectedCount', { count: selectedRowKeys.length })}
              </Text>
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={batchDownloading}
                onClick={handleBatchDownload}
              >
                {t('documentLibrary.batchDownloadZip')}
              </Button>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                {t('documentLibrary.clearSelection')}
              </Button>
            </>
          )}
        </div>

        <Spin spinning={loading}>
          {viewMode === 'list' ? (
            <Table
              dataSource={items}
              columns={columns}
              rowKey="id"
              tableLayout="fixed"
              scroll={{ x: 980 }}
              style={{ width: '100%' }}
              className="document-library-table"
              rowSelection={{
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys),
                getCheckboxProps: () => ({}),
              }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: false,
                showTotal: (rowTotal) => t('documentLibrary.totalRows', { count: rowTotal }),
                onChange: (p) => { setPage(p); setSelectedRowKeys([]); loadItems(p); },
              }}
              size="middle"
              locale={{ emptyText: <Empty description={t('documentLibrary.noDocuments')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            />
          ) : (
            <>
              <GridView />
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Button
                  disabled={page >= Math.ceil(total / pageSize)}
                  onClick={() => { const np = page + 1; setPage(np); loadItems(np); }}
                >
                  {t('documentLibrary.loadMore')}
                </Button>
              </div>
            </>
          )}
        </Spin>
        </Content>
      </div>

      {/* 全螢幕開啟文件 Modal */}
      <ArtifactFullscreenModal item={openItem} onClose={() => setOpenItem(null)} t={t} />
    </Layout>
  );
}
